import type { BoardPoint, FoxKifuRecordData, StoneColor } from '@wx-weiqi/contracts';
import { classroomReplayFrames } from './classroom-sgf-presentation';
import type { ClassroomFrame } from './classroom-lecture';

export type ClassroomFrames = readonly ClassroomFrame[];

export interface ClassroomBranch {
  readonly id: string;
  readonly label: string;
  readonly kind: 'SOURCE' | 'VARIATION';
  readonly frames: ClassroomFrames;
  readonly cursor: number;
  readonly originId: string;
  readonly originCursor: number;
  readonly undo: readonly ClassroomBranchPosition[];
}

export interface ClassroomBranchPosition {
  readonly frames: ClassroomFrames;
  readonly cursor: number;
}

export interface ClassroomBranchNavigation extends ClassroomBranchPosition {
  readonly workspace: ClassroomBranches;
}

export interface ClassroomBranches {
  readonly branches: readonly ClassroomBranch[];
  readonly activeId: string;
}

export function createClassroomBranches(
  record: FoxKifuRecordData,
  initialPlayer: StoneColor,
): ClassroomBranches {
  const replays = new Map([[0, classroomReplayFrames(record, initialPlayer)]]);
  for (const branch of record.sourceBranches ?? []) {
    const parent = replays.get(branch.parentId);
    if (parent === undefined) throw new Error('棋谱分支缺少起点');
    replays.set(
      branch.id,
      classroomReplayFrames(
        { ...record, frames: branch.frames },
        initialPlayer,
        parent.slice(0, branch.baseMoveNumber + 1),
      ),
    );
  }
  return {
    activeId: 'source-0',
    branches: [...replays].map(([id, frames]) => ({
      id: `source-${id}`,
      label: id === 0 ? '主分支' : `主分支 · 原谱 ${id + 1}`,
      kind: 'SOURCE',
      frames,
      cursor: 0,
      originId: `source-${id}`,
      originCursor: 0,
      undo: [],
    })),
  };
}

/** Only one ephemeral variation exists; original frames are never rewritten. */
export function startClassroomVariation(
  workspace: ClassroomBranches,
  cursor: number,
): ClassroomBranches {
  const active = workspace.branches.find((branch) => branch.id === workspace.activeId);
  if (active === undefined || active.kind === 'VARIATION') return workspace;
  return {
    activeId: 'variation',
    branches: [
      ...workspace.branches.map((branch) => (branch === active ? { ...branch, cursor } : branch)),
      {
        id: 'variation',
        label: '课堂变化',
        kind: 'VARIATION',
        frames: active.frames.slice(0, cursor + 1),
        cursor,
        originId: active.id,
        originCursor: cursor,
        undo: [],
      },
    ],
  };
}

export function editClassroomBranch(
  workspace: ClassroomBranches,
  frames: ClassroomFrames,
  baseCursor: number,
  nextCursor: number,
): ClassroomBranches {
  const next = startClassroomVariation(workspace, baseCursor);
  return {
    ...next,
    branches: next.branches.map((branch) =>
      branch.id !== next.activeId
        ? branch
        : {
            ...branch,
            frames,
            cursor: nextCursor,
            undo: [...branch.undo, { frames: branch.frames, cursor: baseCursor }],
          },
    ),
  };
}

/** Leaving a trial discards it; only imported routes are selectable. */
export function selectClassroomBranch(
  workspace: ClassroomBranches,
  id: string,
  cursor: number,
): ClassroomBranches {
  if (!workspace.branches.some((branch) => branch.id === id && branch.kind === 'SOURCE'))
    return workspace;
  return {
    activeId: id,
    branches: workspace.branches
      .filter((branch) => branch.kind === 'SOURCE')
      .map((branch) => (branch.id === workspace.activeId ? { ...branch, cursor } : branch)),
  };
}

function restoreSource(
  workspace: ClassroomBranches,
  variation: ClassroomBranch,
  cursor: number,
): ClassroomBranchNavigation {
  const next = selectClassroomBranch(workspace, variation.originId, variation.originCursor);
  const source = next.branches.find((branch) => branch.id === next.activeId)!;
  return {
    workspace: next,
    frames: source.frames,
    cursor: Math.max(0, Math.min(source.frames.length - 1, cursor)),
  };
}

/** Undo an edit, including setup edits which did not increment the move counter. */
export function previousClassroomBranch(
  workspace: ClassroomBranches,
  cursor: number,
): ClassroomBranchNavigation {
  const active = workspace.branches.find((branch) => branch.id === workspace.activeId)!;
  if (active.kind === 'SOURCE')
    return { workspace, frames: active.frames, cursor: Math.max(0, cursor - 1) };
  if (active.undo.length <= 1) return restoreSource(workspace, active, active.originCursor);
  const previous = active.undo.at(-1)!;
  return {
    workspace: {
      ...workspace,
      branches: workspace.branches.map((branch) =>
        branch === active ? { ...branch, ...previous, undo: branch.undo.slice(0, -1) } : branch,
      ),
    },
    ...previous,
  };
}

export function seekClassroomBranch(
  workspace: ClassroomBranches,
  cursor: number,
  target: number,
  discardAtOrigin = false,
): ClassroomBranchNavigation {
  const active = workspace.branches.find((branch) => branch.id === workspace.activeId)!;
  const clamped = Math.max(0, Math.min(active.frames.length - 1, Math.trunc(target)));
  if (active.kind === 'SOURCE') return { workspace, frames: active.frames, cursor: clamped };
  if (clamped >= cursor && !discardAtOrigin) return { workspace, frames: active.frames, cursor };
  if (clamped <= active.originCursor) return restoreSource(workspace, active, clamped);
  let result = { workspace, frames: active.frames, cursor };
  while (result.cursor > clamped) result = previousClassroomBranch(result.workspace, result.cursor);
  return result;
}

/** Follow an already validated original move only while the replay prefix still matches. */
export function followClassroomSourceMove(
  workspace: ClassroomBranches,
  cursor: number,
  point: BoardPoint,
  color: StoneColor,
): ClassroomBranchNavigation | null {
  const active = workspace.branches.find((branch) => branch.id === workspace.activeId)!;
  if (active.kind === 'VARIATION' && active.undo.length > 0) return null;
  const original =
    active.kind === 'SOURCE'
      ? active
      : workspace.branches.find((branch) => branch.id === active.originId)!;
  const candidates = [
    original,
    ...workspace.branches.filter((branch) => branch.kind === 'SOURCE' && branch !== original),
  ];
  const matching = candidates.find((branch) => {
    const next = branch.frames[cursor + 1];
    return (
      next?.color === color &&
      next.lastMove?.x === point.x &&
      next.lastMove.y === point.y &&
      branch.frames.slice(0, cursor + 1).every((frame, index) => frame === original.frames[index])
    );
  });
  if (matching === undefined) return null;
  return {
    workspace: selectClassroomBranch(workspace, matching.id, cursor),
    frames: matching.frames,
    cursor: cursor + 1,
  };
}
